import HighlightOff from "@mui/icons-material/HighlightOff"
import { useMediaQuery, useTheme } from "@mui/material"
import LinearProgress from "@mui/material/LinearProgress"

import { ChangeEvent, FC, useEffect, useMemo, useState } from "react"

import { CardTable } from "src/components/common/cardTable"
import { Select } from "src/components/common/select"
import { Table, TableData } from "src/components/common/table"

import { filterSelecableBookings } from "src/components/core/header"
import { create } from "src/helpers/bem"

import { useTranslation } from "src/hooks/translation"
import {
  deletePermission,
  getPermissionBySalesorderId,
} from "src/queriesXRM/permission"
import { useAccount } from "src/states/account"
import { useBooking } from "src/states/bookingDetail"
import {
  mergeBookingDetailPermissions,
  removeBookingDetailPermission,
  useBookingDetailPermissions,
} from "src/states/bookingDetailPermissions"

import { useBookings } from "src/states/bookings"
import { TranslationMessages } from "src/translations"

import styles from "./PermissionTable.module.scss"
import { PermissionTableActions } from "./PermissionTableActions"

const bem = create(styles, "PermissionTable")

export type PermissionTableProps = {
  messages: TranslationMessages
}

export const PermissionTable: FC<PermissionTableProps> = ({ messages }) => {
  const m = messages.pages.booking.detail.actions.permissions
  const [loading, setLoading] = useState(true)
  const account = useAccount()
  const { booking } = useBooking()
  const { bookings } = useBookings()
  const { formatMessage } = useTranslation()
  const { permissions, permissionTableUser, currentSalesorderId, open } =
    useBookingDetailPermissions()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("xl"))

  const currentBooking = bookings.find((b) => b.id === booking?.id)
  const formattedBookings = bookings
    .filter(filterSelecableBookings)
    .sort((a, b) => b.name.localeCompare(a.name))
    .map((booking) => {
      const standhall = booking.standhall
        ? m.selectBookingStandhall + booking.standhall
        : ""
      const standnumber = booking.standnumber
        ? m.selectBookingStandnumber + booking.standnumber
        : ""
      return {
        key: booking.id,
        value: `${booking.name!} | ${
          booking.exhibitor
        }${standhall}${standnumber}`,
      }
    })
  const cellTranslations = {
    ...m.table,
    role: {
      ...m.table.role,
      items: {
        ...m.table.role.items,
        BuyOnForeignAccount: formatMessage(
          m.table.role.items.BuyOnForeignAccount1,
          { company: currentBooking?.exhibitor },
        ),
      },
    },
  }

  const getPermissions = async () => {
    await getPermissionBySalesorderId(currentSalesorderId).then((res) => {
      if (Array.isArray(res)) {
        if (permissionTableUser === "MainExhibitor") {
          mergeBookingDetailPermissions({
            permissions: res,
          })
        } else {
          mergeBookingDetailPermissions({
            permissions: res.filter(
              (permission) => permission.role != "Administrator",
            ),
          })
        }
      }
    })
  }

  useEffect(() => {
    if (open) {
      if (permissionTableUser === "MainExhibitor") {
        mergeBookingDetailPermissions({
          currentSalesorderId: booking?.id as string,
        })
      } else {
        mergeBookingDetailPermissions({
          currentSalesorderId: formattedBookings[0].key as string,
        })
      }
    }
  }, [open])

  useEffect(() => {
    setLoading(true)
    getPermissions().finally(() => {
      setLoading(false)
    })
  }, [currentSalesorderId])

  const handleSearchTable = (value: string, data: TableData[]) => {
    const Value = value.toLowerCase()
    return data.filter((item) => {
      if (!Value) return true
      if (
        item.name?.toLowerCase()?.includes(Value) ||
        (item.email as string)?.toLowerCase()?.includes(Value) ||
        (item.emailForeignAccount as string)?.toLowerCase()?.includes(Value) ||
        (typeof item?.name === "string" &&
          item?.name?.toLowerCase()?.includes(Value))
      ) {
        return true
      }
    })
  }

  const handleChangeSelect = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const id = e?.target?.value as string
    mergeBookingDetailPermissions({
      currentSalesorderId: id,
    })
  }

  const handleDeleteRole = async (e: TableData) => {
    setLoading(true)
    await deletePermission(e.id as string)
      .then(() => {
        removeBookingDetailPermission(e.id as string)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const formatedPermissions = useMemo(
    () =>
      permissions
        .filter((p) => p.email !== account.email)
        .map((el) => ({
          id: el.id,
          name: el.name ?? "-",
          email: el.email,
          role: el.role,
          emailForeignAccount: el.emailForeignAccount ?? "-",
        })),
    [account.email, permissions],
  ) as TableData[]

  const renderMobileTable = () => {
    return (
      <CardTable
        getKey={(item, index) => item.name}
        items={formatedPermissions}
        onDelete={handleDeleteRole}
        onSearch={handleSearchTable}
      >
        {(item, index) => {
          const keys = Object.keys(item)

          return (
            <>
              {keys.map((k) => {
                const translation = (cellTranslations as any)[k]

                if (translation === undefined) {
                  return
                }
                const value = item[k]

                const displayValue =
                  typeof value === "string" || typeof value === "number"
                    ? (translation.items?.[value] ?? String(value))
                    : ""
                return (
                  <div key={k}>
                    <span>{translation.label}</span>:{" "}
                    <span>{displayValue}</span>
                  </div>
                )
              })}
            </>
          )
        }}
      </CardTable>
    )
  }

  const renderTable = () => {
    if (isMobile) {
      return renderMobileTable()
    }

    return (
      <Table
        showAvatar
        cellTranslations={cellTranslations}
        data={formatedPermissions}
        handleSearch={handleSearchTable}
        messages={messages}
        order="desc"
        orderBy="name"
        iconActions={[
          {
            icon: <HighlightOff />,
            color: "error",
            onClick: handleDeleteRole,
          },
        ]}
      />
    )
  }

  console.log(formatedPermissions)
  if (loading) {
    return <LinearProgress color="primary" />
  }
  return (
    <div className={bem()}>
      <PermissionTableActions messages={messages} />
      {permissionTableUser === "Representative" && (
        <div className={bem("booking__select")}>
          <Select
            helperText={m.selectBookingHelperText}
            items={formattedBookings}
            label={m.selectBookingLabel}
            sizeVariant="large"
            value={currentSalesorderId}
            onChange={handleChangeSelect}
          />
        </div>
      )}
      {formatedPermissions && formatedPermissions.length > 0 ? (
        renderTable()
      ) : (
        <p className={bem("nodata")}>{m.noData}</p>
      )}
    </div>
  )
}
